import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Decode a JWT payload without external libs. Returns null on any error.
const decodeJwt = (token) => {
  try {
    const [, payload] = token.split(".");
    // base64url → base64
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(b64);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

// Compute how long to wait before it is safe to use a freshly issued
// Supabase JWT. Some Supabase Auth servers are ahead of our container
// clock, which causes a transient PGRST303 "JWT issued at future" on the
// first request. We inspect the token's `iat` claim and wait until our
// local clock has caught up, plus a small safety buffer.
const clockSkewDelayMs = (session) => {
  if (!session?.access_token) return 0;
  const claims = decodeJwt(session.access_token);
  if (!claims?.iat) return 0;
  const iatMs = claims.iat * 1000;
  const now = Date.now();
  if (iatMs <= now) return 0;
  return iatMs - now + 500; // + safety buffer
};

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      const wait = clockSkewDelayMs(session);
      const apply = () => {
        if (mounted) {
          setSession(session);
          setLoading(false);
        }
      };
      if (wait > 0) setTimeout(apply, wait);
      else apply();
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, sess) => {
      // On fresh sign-in, wait for our clock to catch up with the token's
      // iat claim so downstream queries don't 401 with PGRST303.
      if (event === "SIGNED_IN") {
        const wait = clockSkewDelayMs(sess);
        if (wait > 0) {
          setTimeout(() => { if (mounted) setSession(sess); }, wait);
        } else {
          setSession(sess);
        }
      } else {
        setSession(sess);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, loading, user: session?.user || null, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
